/**
 * Report Job Service
 * Manages background report generation jobs with persistence and notifications
 */

export interface ReportJob {
  id: string
  type: string
  title: string
  status: 'pending' | 'processing' | 'completed' | 'error'
  progress: number
  createdAt: string
  completedAt?: string
  error?: string
  data?: any
  metadata?: any
  params: {
    filter?: string
    start_date?: string
    end_date?: string
    device_id?: string
    region?: string
    [key: string]: any
  }
}

class ReportJobService {
  private static readonly STORAGE_KEY = 'valt_report_jobs'
  private static readonly ACTIVE_JOBS_KEY = 'valt_active_report_jobs'

  private get activeJobsKey(): string {
    return ReportJobService.ACTIVE_JOBS_KEY
  }
  private activeJobs: Map<string, ReportJob> = new Map()
  private pollingIntervals: Map<string, NodeJS.Timeout> = new Map()
  private notificationCallbacks: Array<(job: ReportJob) => void> = []

  constructor() {
    this.loadActiveJobs()
    this.resumeActiveJobs()
  }

  /**
   * Load active jobs from localStorage
   */
  private loadActiveJobs(): void {
    try {
      const stored = localStorage.getItem(this.activeJobsKey)
      if (stored) {
        const jobs = JSON.parse(stored) as ReportJob[]
        jobs.forEach((job) => {
          if (job.status === 'processing' || job.status === 'pending') {
            this.activeJobs.set(job.id, job)
          }
        })
      }
    } catch (error) {
      console.error('Failed to load active jobs:', error)
    }
  }

  /**
   * Save active jobs to localStorage
   */
  private saveActiveJobs(): void {
    try {
      const jobs = Array.from(this.activeJobs.values())
      localStorage.setItem(this.activeJobsKey, JSON.stringify(jobs))
    } catch (error) {
      console.error('Failed to save active jobs:', error)
    }
  }

  /**
   * Resume active jobs on page load
   */
  private resumeActiveJobs(): void {
    this.activeJobs.forEach((job, id) => {
      if (job.status === 'processing' || job.status === 'pending') {
        if (job.type === 'odometer_reading') {
          this.resumeOdometerJob(job)
        } else if (job.type === 'finance_ledger') {
          this.resumeFinanceLedgerJob(job)
        }
      }
    })
  }

  /**
   * Create a new report job
   */
  createJob(type: string, title: string, params: any): string {
    const job: ReportJob = {
      id: `job_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      type,
      title,
      status: 'pending',
      progress: 0,
      createdAt: new Date().toISOString(),
      params,
    }

    this.activeJobs.set(job.id, job)
    this.saveActiveJobs()
    return job.id
  }

  /**
   * Update job status
   */
  updateJob(id: string, updates: Partial<ReportJob>): void {
    const job = this.activeJobs.get(id)
    if (job) {
      Object.assign(job, updates)
      if (updates.status === 'completed' || updates.status === 'error') {
        job.completedAt = new Date().toISOString()
      }
      this.activeJobs.set(id, job)
      this.saveActiveJobs()

      // Notify callbacks
      this.notificationCallbacks.forEach((callback) => callback(job))
    }
  }

  /**
   * Get job by ID
   */
  getJob(id: string): ReportJob | undefined {
    return this.activeJobs.get(id)
  }

  /**
   * Get all active jobs
   */
  getActiveJobs(): ReportJob[] {
    return Array.from(this.activeJobs.values())
  }

  /**
   * Get jobs by type
   */
  getJobsByType(type: string): ReportJob[] {
    return Array.from(this.activeJobs.values()).filter((job) => job.type === type)
  }

  /**
   * Complete a job
   */
  completeJob(id: string, data: any, metadata?: any): void {
    this.updateJob(id, {
      status: 'completed',
      progress: 100,
      data,
      metadata,
    })
    this.stopPolling(id)
  }

  /**
   * Mark job as error
   */
  errorJob(id: string, error: string): void {
    this.updateJob(id, {
      status: 'error',
      error,
    })
    this.stopPolling(id)
  }

  /**
   * Start polling for odometer job
   */
  async startOdometerJob(jobId: string, params: any): Promise<void> {
    const job = this.activeJobs.get(jobId)
    if (!job) return

    this.updateJob(jobId, { status: 'processing', progress: 0 })

    // Import OdometerService dynamically to avoid circular dependencies
    const { OdometerService, flattenOdometerSummaryPreviewRows } = await import('./odometerService')

    // Start progress indicator
    let progressValue = 0
    const progressInterval = setInterval(() => {
      if (progressValue < 90) {
        progressValue = Math.min(progressValue + 2, 90)
        this.updateJob(jobId, { progress: progressValue })
      }
    }, 1000)

    this.pollingIntervals.set(jobId, progressInterval)

    try {
      const toDate = (input: string) => {
        const d = new Date(input)
        return Number.isNaN(d.getTime()) ? new Date() : d
      }

      const formatDate = (d: Date) => d.toISOString().split('T')[0]
      const addDays = (d: Date, days: number) => {
        const copy = new Date(d)
        copy.setDate(copy.getDate() + days)
        return copy
      }

      const toWindow = (filter?: string) => {
        const today = new Date()
        const yesterday = addDays(today, -1)

        switch (filter) {
          case 'today': {
            const day = formatDate(today)
            return { start: `${day} 22:00:00`, end: `${day} 22:00:00` }
          }
          case 'yesterday': {
            const day = formatDate(yesterday)
            return { start: `${day} 22:00:00`, end: `${day} 22:00:00` }
          }
          case 'last_7_days': {
            return { start: `${formatDate(addDays(today, -7))} 22:00:00`, end: `${formatDate(today)} 22:00:00` }
          }
          case 'last_30_days': {
            return {
              start: `${formatDate(addDays(today, -30))} 22:00:00`,
              end: `${formatDate(today)} 22:00:00`,
            }
          }
          case 'this_week': {
            const weekday = today.getDay()
            const mondayOffset = weekday === 0 ? -6 : 1 - weekday
            return {
              start: `${formatDate(addDays(today, mondayOffset))} 22:00:00`,
              end: `${formatDate(today)} 22:00:00`,
            }
          }
          case 'this_month': {
            const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1)
            return { start: `${formatDate(startOfMonth)} 22:00:00`, end: `${formatDate(today)} 22:00:00` }
          }
          default:
            return {}
        }
      }

      const customStart = params.start_date ? `${formatDate(toDate(params.start_date))} 22:00:00` : undefined
      const customEnd = params.end_date ? `${formatDate(toDate(params.end_date))} 22:00:00` : undefined
      const presetWindow = toWindow(params.filter)

      const requestParams = {
        connection: 'telemetry',
        start: customStart || presetWindow.start,
        end: customEnd || presetWindow.end,
        limit: params.limit,
        device_id: params.device_id,
      }

      const result = await OdometerService.generateSummaryReport(requestParams)

      const flattenedData = flattenOdometerSummaryPreviewRows(result)

      clearInterval(progressInterval)
      this.pollingIntervals.delete(jobId)

      this.completeJob(jobId, flattenedData, {
        filter: params.filter,
        date_range: result.window,
        total_records: result.count,
        region: result.connection || params.region,
      })
    } catch (error) {
      clearInterval(progressInterval)
      this.pollingIntervals.delete(jobId)

      const errorMessage = error instanceof Error ? error.message : 'Failed to generate report'
      this.errorJob(jobId, errorMessage)
    }
  }

  /**
   * Resume odometer job polling
   */
  private async resumeOdometerJob(job: ReportJob): Promise<void> {
    if (job.type !== 'odometer_reading') return

    this.updateJob(job.id, { status: 'processing' })
    await this.startOdometerJob(job.id, job.params)
  }

  /**
   * Resume finance ledger job
   */
  private async resumeFinanceLedgerJob(job: ReportJob): Promise<void> {
    if (job.type !== 'finance_ledger') return

    const { financeLedgerJobService } = await import('./financeLedgerJobService')
    financeLedgerJobService.resumeJob(job)
  }

  /**
   * Start finance ledger batch job
   */
  async startFinanceLedgerJob(jobId: string, params: any): Promise<void> {
    const { financeLedgerJobService } = await import('./financeLedgerJobService')
    await financeLedgerJobService.startJob(jobId, params)
  }

  /**
   * Stop polling for a job
   */
  stopPolling(id: string): void {
    const interval = this.pollingIntervals.get(id)
    if (interval) {
      clearInterval(interval)
      this.pollingIntervals.delete(id)
    }
  }

  /**
   * Remove a job
   */
  removeJob(id: string): void {
    this.stopPolling(id)
    this.activeJobs.delete(id)
    this.saveActiveJobs()
  }

  /**
   * Clear completed jobs
   */
  clearCompletedJobs(): void {
    const toRemove: string[] = []
    this.activeJobs.forEach((job, id) => {
      if (job.status === 'completed' || job.status === 'error') {
        toRemove.push(id)
      }
    })
    toRemove.forEach((id) => this.removeJob(id))
  }

  /**
   * Subscribe to job updates
   */
  onJobUpdate(callback: (job: ReportJob) => void): () => void {
    this.notificationCallbacks.push(callback)
    return () => {
      const index = this.notificationCallbacks.indexOf(callback)
      if (index > -1) {
        this.notificationCallbacks.splice(index, 1)
      }
    }
  }
}

// Export singleton instance
export const reportJobService = new ReportJobService()
